import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ColumnDefinition } from 'src/app/dto/columnDefinition';
import { DealerEmployee } from 'src/app/models/dealer-employee.model';
import { Group } from 'src/app/models/group.model';
import { Report } from 'src/app/models/report.model';
import { User } from 'src/app/models/user.model';
import { ClickStreamService } from 'src/app/services/click-stream.service';
import { DealersService } from 'src/app/services/dealers.service';
import { ReportsService } from 'src/app/services/reports.service';
import { UserService } from 'src/app/services/user.service';
import { plaintext } from "../../../assets/plaintext";

@Component({
  selector: 'app-manage-report-security',
  templateUrl: './manage-report-security.component.html',
  styleUrls: ['./manage-report-security.component.scss']
})
export class ManageReportSecurityComponent implements OnInit {
  addSelection: SelectionModel<any> = new SelectionModel(true, []);
  removeSelection: SelectionModel<any> = new SelectionModel(true, []);

  report: Report;

  isLoading = true;
  isGroupsWithAccessLoading = true;
  isDealersLoading = true;
  isPSCUser = false;

  employeeSearchInput: string = '';

  groupsWithAccess: any[];
  idsToFilter: string[];

  employees: DealerEmployee[];
  baseEmployees: DealerEmployee[];

  clickstreamPageTitle = 'Manage Security';

  createSecurityLoading = false;

  saveError = '';

  user: User;
  plaintext = plaintext;

  employeeColumns: ColumnDefinition[];
  employeesWithAccessColumns: ColumnDefinition[];

  constructor(private reportService: ReportsService, private dealersService: DealersService, private userService: UserService, public dialog: MatDialog,
    private router: Router, private route: ActivatedRoute, private _snackBar: MatSnackBar, private clickstream: ClickStreamService) {
    this.user = this.userService.user;

    this.employeeColumns = [
      { columnDef: 'name', header: plaintext.table.name[this.user.language], cell: (w: any) => `${w.name}` },
      { columnDef: 'gmid', header: 'ID', cell: (w: any) => `${w.gmid}` },
      { columnDef: 'gmvssmbusinessroledn', header: plaintext.table.roles[this.user.language], cell: (w: any) => `${w.gmvssmbusinessroledn}` }
    ];

    this.employeesWithAccessColumns = [
      { columnDef: 'name', header: plaintext.table.name[this.user.language], cell: (w: any) => `${w.employee.name}` },
      { columnDef: 'gmid', header: 'ID', cell: (w: any) => `${w.employee.gmid}` },
      { columnDef: 'gmvssmbusinessroledn', header: plaintext.table.roles[this.user.language], cell: (w: any) => `${w.employee.gmvssmbusinessroledn}` }
    ];
  }

  ngOnInit(): void {
    this.isPSCUser = (this.userService.user.isPSC);
    // this.isPSCUser = true;

    this.report = this.reportService.reportState; // attempt to get it from the state first

    if (!this.report) {
      var reportId = this.route.snapshot.paramMap.get('id');
      this.reportService.getReport(reportId).subscribe(reportData => {
        this.report = reportData;
        this.isLoading = false;
        this.fetchSecurity();
      })
    } else {
      this.isLoading = false;
      this.fetchSecurity();
    }
  }

  fetchSecurity(): void {
    this.isGroupsWithAccessLoading = true;
    this.reportService.getReportSecurity(this.report.guid).subscribe(res => {
      this.isGroupsWithAccessLoading = false;
      this.groupsWithAccess = res.hasAccess;
      this.idsToFilter = res.hasAccess.map(g => g.externalId);
      this.fetchGroupsToAdd();
    });
  }

  fetchGroupsToAdd(): void {
    this.isDealersLoading = true;
    this.dealersService.getEmployeesForDealership().subscribe(employees => {
      this.employees = employees.filter(e => !this.groupsWithAccess.some(g => g.externalId.toLowerCase() == e.gmid.toString().toLowerCase()) && this.userService.user.gmid != e.gmid && e.gmvssmadminroledn == 'USER');
      this.baseEmployees = this.employees;
      this.isDealersLoading = false;
    })
  }

  createReportSecurity() {
    var groups = this.addSelection.selected;
    this.createSecurityLoading = true;
    this.reportService.grantReportSecurityPSC(groups, this.report.guid).subscribe(addedGroups => {
      this.reportService.getReportSecurity(this.report.guid).subscribe(reportGroups => {
        this.groupsWithAccess = reportGroups.hasAccess;
        this.employees = this.employees.filter(e => !reportGroups.hasAccess.some(g => g.externalId.toLowerCase() == e.gmid.toString().toLowerCase()));
        this.baseEmployees = this.employees;
        this.createSecurityLoading = false;
      })
      const snackMsg = groups.length > 1 ? plaintext.manageReportSecurity.easPlural[this.user.language] : plaintext.manageReportSecurity.eas[this.user.language];
      this._snackBar.open(snackMsg, plaintext.close[this.user.language], { duration: 2000 });
      this.addSelection.clear();
    }, error => {
      this._snackBar.open(plaintext.manageReportSecurity.failed[this.user.language], plaintext.close[this.user.language], { duration: 3000 });
      this.createSecurityLoading = false;
    });
    this.clickstream.send('add-security', 'click', this.clickstreamPageTitle, 'Add Button', '', true);
  }

  removeReportSecurity() {
    var insightAccessGuids = this.removeSelection.selected.map(g => g.guid);
    this.isGroupsWithAccessLoading = true;
    this.reportService.removeReportSecurity(insightAccessGuids).subscribe(response => {
      this.isGroupsWithAccessLoading = false;
      this.groupsWithAccess = this.groupsWithAccess.filter(g => !insightAccessGuids.some(removedGuid => removedGuid == g.guid));
      const snackMsg = insightAccessGuids.length > 1 ? plaintext.manageReportSecurity.ersPlural[this.user.language] : plaintext.manageReportSecurity.ers[this.user.language];
      this._snackBar.open(snackMsg, plaintext.close[this.user.language], { duration: 2000 });
      this.employees = this.removeSelection.selected.map(g => g.employee).concat(this.employees);
      this.baseEmployees = this.employees;
      this.removeSelection.clear();
    }, error => {
      this._snackBar.open(plaintext.manageReportSecurity.failed[this.user.language], plaintext.close[this.user.language], { duration: 3000 });
      this.isGroupsWithAccessLoading = false;
    })
    this.clickstream.send('remove-security', 'click', this.clickstreamPageTitle, 'Remove Button', '', true);
  }

  deselectGroup(group) {
    this.addSelection.deselect(group);
  }

  searchEmployees(query) {
    const filterBy = query.toLowerCase();
    this.employees = this.baseEmployees.filter(e => e.name.toLowerCase().indexOf(filterBy) != -1 || e.gmvssmbusinessroledn.toLowerCase().indexOf(filterBy) != -1)
  }

  refreshEmpoloyees() {
    this.isDealersLoading = true;
    this.dealersService.getEmployeesForDealership(null, true).subscribe(employees => {
      this.employees = employees.filter(e => !this.groupsWithAccess.some(g => g.externalId == e.gmid.toString()) && this.userService.user.gmid != e.gmid);
      this.baseEmployees = this.employees;
      this.isDealersLoading = false;
    });
  }
}